import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { CATEGORIES } from "@/lib/constants";
import { toast } from "sonner";

const SAMPLE_IMAGES = [
  "https://images.unsplash.com/photo-1658485322290-85d2e8c8460b?w=1200&auto=format&fit=crop",
  "https://images.pexels.com/photos/31585091/pexels-photo-31585091.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "https://images.pexels.com/photos/29069019/pexels-photo-29069019.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "https://images.pexels.com/photos/4028958/pexels-photo-4028958.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "https://images.pexels.com/photos/30037318/pexels-photo-30037318.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "https://images.pexels.com/photos/30412602/pexels-photo-30412602.jpeg?auto=compress&cs=tinysrgb&w=1200",
];

export default function CommunityScience() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({
    category: "wildlife", location: "", description: "", image_url: "",
    guest_name: "", guest_email: "",
  });
  const [busy, setBusy] = useState(false);

  const load = () => {
    http.get(`/observations${filter ? `?category=${filter}` : ""}`)
      .then(r => setItems(r.data.observations || []))
      .catch(() => {});
  };
  useEffect(load, [filter]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        category: form.category,
        location: form.location,
        description: form.description,
        image_url: form.image_url || SAMPLE_IMAGES[Math.floor(Math.random() * SAMPLE_IMAGES.length)],
      };
      if (!user) {
        if (form.guest_name) payload.guest_name = form.guest_name;
        if (form.guest_email) payload.guest_email = form.guest_email;
      }
      const { data } = await http.post("/observations", payload);
      if (data.moderation_status === "approved") {
        toast.success("Observation archived.");
      } else {
        toast.success("Submitted for review. It'll appear once a moderator approves it.");
      }
      setForm({ ...form, location: "", description: "", image_url: "" });
      load();
    } catch { toast.error("Upload failed."); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="community-section" className="py-24 md:py-40 px-6 md:px-10 max-w-[1600px] mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
        <div className="md:col-span-5">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">/06 · Community Science</div>
          <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter">You are the archive.</h2>
        </div>
        <p className="md:col-span-6 md:col-start-7 self-end text-[color:var(--np-text-2)] text-lg">
          Plants, wildlife, insects, fungi, water, weather — pick a category with one tap and share what you see. Every observation joins a permanent scientific record.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-10">
        <button
          onClick={() => setFilter("")}
          className={`font-mono-tech text-[10px] tracking-[0.3em] uppercase px-4 py-2 border transition-colors ${!filter ? "bg-[color:var(--np-text)] text-[color:var(--np-bg)] border-[color:var(--np-text)]" : "border-black/15 text-[color:var(--np-text-2)] hover:border-[color:var(--np-text)]"}`}
          data-testid="filter-all-btn"
        >All</button>
        {CATEGORIES.map(c => (
          <button
            key={c.key}
            onClick={() => setFilter(c.key)}
            className={`inline-flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase px-4 py-2 border transition-colors ${filter === c.key ? "bg-[color:var(--np-text)] text-[color:var(--np-bg)] border-[color:var(--np-text)]" : "border-black/15 text-[color:var(--np-text-2)] hover:border-[color:var(--np-text)]"}`}
            data-testid={`filter-${c.key}-btn`}
          ><span className="text-base leading-none">{c.emoji}</span>{c.label}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-8">
          {items.length === 0 ? (
            <div className="border border-dashed border-black/20 py-24 text-center text-[color:var(--np-text-2)] font-mono-tech text-sm tracking-widest uppercase">
              No observations yet in this category. Be the first witness.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {items.map((o, i) => (
                <motion.figure
                  key={o.obs_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.4, delay: i * 0.03 }}
                  className="group relative overflow-hidden bg-[color:var(--np-bg-2)] aspect-[3/4]"
                  data-testid={`obs-card-${i}`}
                >
                  {o.image_url && (
                    <img src={o.image_url} alt={o.category} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  )}
                  {o.is_first_in_region && (
                    <div data-testid={`endemic-badge-${i}`} className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 bg-[color:var(--np-brand)] text-white font-mono-tech text-[9px] tracking-[0.25em] uppercase shadow-lg">
                      ★ First in {o.state || o.country || "region"}
                    </div>
                  )}
                  <figcaption className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
                    <div className="font-mono-tech text-[9px] tracking-[0.3em] uppercase text-[color:var(--np-live)]">{o.category}</div>
                    <div className="font-serif-display text-lg leading-tight mt-1 line-clamp-2">{o.description}</div>
                    <div className="font-mono-tech text-[9px] tracking-[0.25em] uppercase text-white/60 mt-1">{o.location} · {o.user_name}</div>
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-4">
          <div className="glass p-6 sticky top-32" data-testid="community-upload-form">
            <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-live)] mb-4">Contribute</div>
            <h3 className="font-serif-display text-3xl mb-2 tracking-tight">Add an observation.</h3>
            {user ? (
              <p className="text-xs text-[color:var(--np-text-2)] mb-4">
                Publishing as <b className="text-[color:var(--np-text)]">{user.name}</b>. Approved instantly.
              </p>
            ) : (
              <p className="text-xs text-[color:var(--np-text-2)] mb-4">
                Publishing anonymously — your submission enters the moderation queue and appears once a curator approves it. Optionally add a name/email so we can credit you and reach you if we need clarification.
              </p>
            )}

            <form onSubmit={submit} className="space-y-4">
              <div>
                <div className="font-mono-tech text-[9px] tracking-[0.3em] uppercase text-[color:var(--np-muted)] mb-2">Category · one tap</div>
                <div className="grid grid-cols-3 gap-1.5" data-testid="obs-category-chips">
                  {CATEGORIES.map(c => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setForm({ ...form, category: c.key })}
                      aria-pressed={form.category === c.key}
                      className={`flex flex-col items-center justify-center gap-1 px-2 py-3 border transition-colors ${
                        form.category === c.key
                          ? "bg-[color:var(--np-text)] text-[color:var(--np-bg)] border-[color:var(--np-text)]"
                          : "bg-[color:var(--np-bg)] border-black/15 text-[color:var(--np-text-2)] hover:border-[color:var(--np-brand)] hover:text-[color:var(--np-text)]"
                      }`}
                      data-testid={`obs-cat-${c.key}`}
                    >
                      <span className="text-xl leading-none">{c.emoji}</span>
                      <span className="font-mono-tech text-[9px] tracking-[0.15em] uppercase leading-none">{c.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {!user && (
                <div className="grid grid-cols-2 gap-3">
                  <input
                    value={form.guest_name}
                    onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                    placeholder="Your name (optional)"
                    className="w-full bg-transparent border-b border-black/15 focus:border-[color:var(--np-live)] outline-none py-2 font-mono-tech text-xs"
                    data-testid="obs-guest-name"
                    maxLength={80}
                  />
                  <input
                    type="email"
                    value={form.guest_email}
                    onChange={(e) => setForm({ ...form, guest_email: e.target.value })}
                    placeholder="Email (optional)"
                    className="w-full bg-transparent border-b border-black/15 focus:border-[color:var(--np-live)] outline-none py-2 font-mono-tech text-xs"
                    data-testid="obs-guest-email"
                    maxLength={120}
                  />
                </div>
              )}

              <input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location (e.g., Manali, IN)" className="w-full bg-transparent border-b border-black/15 focus:border-[color:var(--np-live)] outline-none py-2 font-mono-tech text-sm" data-testid="obs-location-input" />
              <textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What did you see?" rows={4} className="w-full bg-transparent border border-black/15 focus:border-[color:var(--np-live)] outline-none p-3 text-sm resize-none" data-testid="obs-description-input" />
              <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Photo URL (optional)" className="w-full bg-transparent border-b border-black/15 focus:border-[color:var(--np-live)] outline-none py-2 font-mono-tech text-xs" data-testid="obs-image-input" />
              <button disabled={busy} className="w-full font-mono-tech text-[11px] tracking-[0.3em] uppercase border border-black/15 hover:bg-[color:var(--np-text)] hover:text-[color:var(--np-bg)] px-6 py-3 transition-colors" data-testid="obs-submit-btn">
                {busy ? "Archiving…" : user ? "Publish" : "Submit for review"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
